import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {IoCheckmarkOutline, IoCloseOutline, IoSearchOutline} from 'react-icons/io5';
import {normalizeAthleteSearch} from '../utils/athleteSearch';
import type {AthleteItem} from '../services/api';
import {AthleteSettingsButton} from './AthleteSettingsButton';
import './compact-athlete-picker.css';

/** Native dialog supplies focus containment, Escape dismissal and focus restoration. */
export function CompactAthletePicker({athletes, selectedId, top, onPick, onClose, onOpenSettings}: {
  athletes: AthleteItem[]; selectedId: number | null; top: number;
  onPick: (id: number) => void; onClose: () => void;
  onOpenSettings?: (id: number) => void;
}) {
  const {t, i18n} = useTranslation();
  const dialog = useRef<HTMLDialogElement>(null);
  const [search, setSearch] = useState('');
  const filtered = athletes.filter(athlete => normalizeAthleteSearch(athlete.full_name).includes(normalizeAthleteSearch(search)));
  useEffect(() => {
    const panel = dialog.current;
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    panel?.showModal();
    document.body.style.overflow = 'hidden';
    return () => { panel?.close(); document.body.style.overflow = overflow; previous?.focus(); };
  }, []);
  return <dialog ref={dialog} className="compact-athlete-picker" aria-label={t('athletePicker.title')}
    dir={i18n.dir()} style={{top: `min(${top}px, 25dvh)`}}
    onCancel={event => {event.preventDefault(); onClose();}}
    onClick={event => {if(event.target === event.currentTarget) {
      const rect = event.currentTarget.getBoundingClientRect();
      if(event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) onClose();
    }}}>
    <div className="compact-athlete-picker__search">
      <IoSearchOutline aria-hidden="true"/>
      <input autoFocus type="text" role="searchbox" autoComplete="off" value={search} onChange={event => setSearch(event.target.value)}
        placeholder={t('athletePicker.search')} aria-label={t('athletePicker.search')}/>
      <button type="button" aria-label={t('common.close')} onClick={onClose}><IoCloseOutline aria-hidden="true"/></button>
    </div>
    <div className="compact-athlete-picker__list">
      {filtered.map(athlete => <div key={athlete.id} className={`compact-athlete-picker__row${athlete.id === selectedId ? ' is-selected' : ''}`}>
        <button type="button" className="compact-athlete-picker__pick" aria-pressed={athlete.id === selectedId} onClick={() => onPick(athlete.id)}>
          <span>{athlete.full_name}</span>{athlete.id === selectedId && <IoCheckmarkOutline aria-hidden="true"/>}
        </button>
        {onOpenSettings && <AthleteSettingsButton title={t('app.athleteSettings')} onClick={() => onOpenSettings(athlete.id)}/>}
      </div>)}
      {!filtered.length && <p className="compact-athlete-picker__empty" role="status">{t(athletes.length ? 'athletePicker.noResults' : 'app.noAthletesLinked')}</p>}
    </div>
  </dialog>;
}
